import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DoDisturbOutlinedIcon from "@mui/icons-material/DoDisturbOutlined";
import { StyledTableCell, StyledTableRow } from "../../../Component/UI/Table/Table";
import classes from "./HTMLReportTab.module.css";

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatDateTime(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function renderChips(items, max = 3) {
  if (!Array.isArray(items) || items.length === 0) return <span>—</span>;
  const visible = items.slice(0, max);
  const remaining = items.slice(max);
  return (
    <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
      {visible.map((item) => (
        <Box
          key={item}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.5,
            borderRadius: "6px",
            border: "1px solid #e5e7eb",
            backgroundColor: "#f9fafb",
            fontSize: "12px",
            fontWeight: 500,
            color: "#374151",
          }}
        >
          {item}
        </Box>
      ))}
      {remaining.length > 0 && (
        <Tooltip
          title={<Box>{remaining.map((r) => <div key={r}>{r}</div>)}</Box>}
          arrow
          placement="top"
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              backgroundColor: "#e5e7eb",
              fontSize: "12px",
              fontWeight: 700,
              color: "#4b5563",
              cursor: "help",
            }}
          >
            +{remaining.length}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

function StatusChip({ status }) {
  if (status === "completed") {
    return <Chip label="Completed" color="success" size="small" />;
  }
  if (status === "failed") {
    return <Chip label="Failed" color="error" size="small" />;
  }
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <CircularProgress size={12} thickness={5} />
      <Chip label="In Progress" color="warning" size="small" />
    </Box>
  );
}

export default function HTMLReportHistoryItem({
  item,
  index,
  onDownload,
  onViewInline,
  onViewInNewTab,
  onCancel,
}) {
  const [downloading, setDownloading] = useState(false);
  const [viewingNew, setViewingNew] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const isCompleted = item.status === "completed";
  const isInProgress = item.status === "in-progress" || item.status === "pending";
  const { selections = {} } = item;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await onDownload(item.jobId, item.transactionId);
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenNew = async () => {
    setViewingNew(true);
    try {
      await onViewInNewTab(item.jobId);
    } finally {
      setViewingNew(false);
    }
  };

  const handleCancelConfirm = async () => {
    setCancelling(true);
    try {
      await onCancel(item.jobId);
      setCancelDialog(false);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <StyledTableRow key={item.jobId || index}>
      {/* Category */}
      <StyledTableCell>
        {selections.category ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
              border: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              fontSize: "12px",
              fontWeight: 500,
              color: "#374151",
            }}
          >
            {selections.category}
          </Box>
        ) : (
          <span>—</span>
        )}
      </StyledTableCell>

      {/* LLMs */}
      <StyledTableCell>{renderChips(selections.llms, 3)}</StyledTableCell>

      {/* Markets */}
      <StyledTableCell>{renderChips(selections.markets, 3)}</StyledTableCell>

      {/* Personas */}
      <StyledTableCell>{renderChips(selections.persona, 3)}</StyledTableCell>

      {/* Date */}
      <StyledTableCell title={formatDateTime(item.createdAt)}>
        {formatDate(item.createdAt)}
      </StyledTableCell>

      {/* Status */}
      <StyledTableCell>
        <StatusChip status={item.status} />
      </StyledTableCell>

      {/* Actions */}
      <StyledTableCell>
        <Box display="flex" alignItems="center" gap={0.5}>
          {isCompleted && (
            <>
              <Tooltip title="View report here">
                <IconButton
                  size="small"
                  className={classes.actionIcon}
                  onClick={() => onViewInline(item.jobId)}
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new browser tab">
                <IconButton
                  size="small"
                  className={classes.actionIcon}
                  onClick={handleOpenNew}
                  disabled={viewingNew}
                >
                  {viewingNew ? (
                    <CircularProgress size={14} />
                  ) : (
                    <OpenInNewIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Download HTML file">
                <IconButton
                  size="small"
                  className={classes.actionIcon}
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  {downloading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <DownloadOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* Cancel (in-progress) or Remove (completed/failed) */}
          <Tooltip title={isInProgress ? "Cancel generation" : "Remove report"}>
            <IconButton
              size="small"
              className={classes.actionIconDanger}
              onClick={() => setCancelDialog(true)}
              disabled={cancelling}
            >
              {cancelling ? (
                <CircularProgress size={14} />
              ) : isInProgress ? (
                <DoDisturbOutlinedIcon fontSize="small" />
              ) : (
                <DeleteOutlineIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </StyledTableCell>

      {/* Cancel / remove confirmation dialog */}
      <Dialog
        open={cancelDialog}
        onClose={() => !cancelling && setCancelDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {isInProgress ? "Cancel Generation?" : "Remove Report?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isInProgress
              ? "This will stop the report generation. The entry will be removed from history."
              : "This will permanently remove the report from history. This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setCancelDialog(false)} disabled={cancelling}>
            Keep
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelConfirm}
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={16} /> : isInProgress ? "Cancel Generation" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledTableRow>
  );
}
